import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { pathFor } from '../routes/paths';
import { useLanguage } from '../contexts/LanguageContext';

const SectionContainer = styled.section`
  padding: 100px 0 80px;
  background: #ffffff;
`;

const Separator = styled.div`
  height: 1px;
  background: #e6e0db;
  max-width: 900px;
  margin: 28px auto 40px;
`;

const ReviewsWrap = styled.div`
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  padding: 0 48px; /* space for arrows */
`;

const Review = styled.div`
  text-align: center;
  margin: 26px auto 32px;
`;

const ReviewText = styled.p`
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  font-size: 18px;
  line-height: 1.9;
  color: #7a7a7a;
`;

const ReviewAuthor = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #b0a6a2;
  margin-top: 10px;
`;

const ReviewViewport = styled.div`
  position: relative;
  min-height: 160px;
`;

const PrevButton = styled.button`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: #9a8f8b;
  font-size: 28px;
  line-height: 1;
  width: 44px;
  height: 44px;
  cursor: pointer;
  border-radius: 50%;
  transition: color 0.2s ease, background 0.2s ease;
  &:hover { color: #67313a; background: rgba(103,49,58,0.08); }
`;

const NextButton = styled(PrevButton)`
  left: auto;
  right: 0;
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 60px;
  @media (max-width: 600px) {
    padding: 0 24px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled(motion.h2)`
  font-family: 'Tiro Bangla', serif;
  font-size: clamp(22px, 4vw, 32px);
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #7a7a7a;
`;

const GalleryRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 60px;
  flex-wrap: wrap;
`;

const GalleryItem = styled(motion.figure)`
  margin: 0;
  text-align: center;
`;

const ImageBox = styled.div`
  width: min(100%, 340px);
  height: 460px;
  overflow: hidden;
  background: #eee;
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const ShootLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;
`;

const Caption = styled.figcaption`
  margin-top: 14px;
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  font-size: 14px;
  color: #a0a0a0;
`;

const Button = styled(motion.button)`
  background: #67313a;
  border: none;
  padding: 14px 35px;
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  display: block;
  margin: 48px 0 36px; /* horizontal centering handled by ButtonLink wrapper */

  &:hover {
    background: #4e1f28;
    transform: translateY(-2px);
  }
`;

const ButtonLink = styled(Link)`
  text-decoration: none;
  display: block;
  width: max-content;
  margin: 0 auto; /* center the wrapper reliably */
`;

const DistinctionsWrap = styled.div`
  margin-top: 80px;
`;

// Label removed per request

const Marquee = styled.div`
  overflow: hidden;
`;

const Track = styled.div`
  display: flex;
  align-items: center;
  gap: 80px;
  width: max-content;
  will-change: transform;
  animation: scroll 95s linear infinite;

  @keyframes scroll {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
`;

const Badge = styled.img`
  height: 50px;
  width: auto;
  opacity: 0.85;
`;

const StylizedShootSection = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });
  const { lang } = useLanguage();
  const SHOOT_TO_SLUG = {
    "Domaine des Saints Peres": 'wedding_domaine_des_saints_peres',
    "Palace de Menthon": 'wedding_menthon_saint_bernard_savoie',
    "Chateau d\'Ailly": 'wedding_chateau_d_ailly',
  };

  const shoots = [
    {
      id: 1,
      caption: "Domaine des Saints Peres",
      image: "/images/DOMAINE DES SAINTS PERES.webp"
    },
    {
      id: 2,
      caption: "Palace de Menthon",
      image: "/images/PALACE DE MENTHON.webp"
    },
    {
      id: 3,
      caption: "Chateau d'Ailly",
      image: "/images/CHATEAU D AILLY.webp"
    }
  ];

  const [currentReview, setCurrentReview] = useState(0);

  const badges = [
    "/images/QUEEN FOR A DAY.gif",
    "/images/MELLE DENTELLE.webp",
    "/images/LME.webp",
    "/images/UN BEAU JOUR.webp",
  ];

  // Build two identical halves to guarantee a perfect loop, then duplicate the pair
  const pair = [...badges, ...badges];
  const repeated = [...pair, ...pair];

  const reviewsEN = [
    {
      text:
        "Ludo is an incredible photographer! He’s caring, warm, and makes everyone feel at ease. His work is perfect — he won us over, and his images speak for themselves. A very soft aesthetic and special attention to detail. On the wedding day Ludo is discreet and friendly; we received many positive comments from our guests. I can only recommend him! Thank you again, Ludo — thanks to you we have the most beautiful memories of our wedding.",
      author: 'Mélody',
    },
    {
      text:
        "Many thanks to Ludovic for his work on Saturday for our wedding at Domaine de Tourieux! Friendly, energetic, and endlessly imaginative, he created unique memories in a joyful atmosphere. He put us at ease in front of the camera and we can’t wait to see the final result! We recommend him with our eyes closed — thank you and see you soon!",
      author: 'Estelle',
    },
    {
      text:
        "We chose Ludovic to accompany us for both our civil and religious weddings. He captured the most beautiful moments of our two days. Attentive to our wishes, he makes you feel comfortable in front of the lens. Whether couple portraits or photos with our loved ones, the result is spontaneous and simply incredible. A talented, very human, and dedicated photographer — we can only recommend him. Thank you again, Ludo.",
      author: 'Aurélie',
    },
    {
      text:
        "Beyond the photos, Ludo supported us from start to finish throughout our wedding planning. His dedication is matched only by his kindness toward us and our guests all day long. Through his personality he made us feel at ease (and that wasn’t easy!) and brought out the atmosphere of the big day in every image. A huge thank‑you for capturing such a special day so beautifully — we’ll be proud to share these moments with our future children thanks to your photos.",
      author: 'Jade',
    },
    {
      text:
        "Our wedding photos are sublime! We received nothing but compliments from our guests. Ludo made us feel comfortable in front of the camera and gave us unforgettable moments during the sessions. I recommend him wholeheartedly for his kindness and the quality of his work! Our albums are incredible — and as a bonus, proudly made in France.",
      author: 'Soline',
    },
  ];
  const reviewsFR = [
    {
      text:
        "Ludo est un photographe incroyable ! Bienveillant, chaleureux, il met tout le monde à l’aise. Son travail est parfait — il nous a conquis et ses images parlent d’elles‑mêmes. Une esthétique douce et une attention particulière aux détails. Le jour J, Ludo est discret et sympathique ; nous avons reçu de nombreux compliments de nos invités. Je le recommande les yeux fermés ! Merci encore Ludo — grâce à toi, nous avons les plus beaux souvenirs de notre mariage.",
      author: 'Mélody',
    },
    {
      text:
        "Un grand merci à Ludovic pour son travail samedi lors de notre mariage au Domaine de Tourieux ! Souriant, énergique et d’une imagination débordante, il a créé des souvenirs uniques dans une atmosphère joyeuse. Il nous a mis à l’aise devant l’objectif et nous avons hâte de découvrir le résultat final ! Nous le recommandons les yeux fermés — merci et à très vite !",
      author: 'Estelle',
    },
    {
      text:
        "Nous avons choisi Ludovic pour nous accompagner lors de notre mariage civil puis religieux. Il a su capter les plus beaux instants de ces deux journées. À l’écoute de nos envies, il met à l’aise devant l’objectif. En couple comme avec nos proches, le résultat est spontané et tout simplement incroyable. Un photographe talentueux, très humain et investi — nous le recommandons vivement. Merci encore, Ludo.",
      author: 'Aurélie',
    },
    {
      text:
        "Au‑delà des photos, Ludo nous a accompagnés du début à la fin dans la préparation du mariage. Son dévouement n’a d’égal que sa gentillesse envers nous et nos invités tout au long de la journée. Par sa personnalité, il nous a mis en confiance (et ce n’était pas gagné !) et a fait ressortir l’atmosphère du grand jour dans chaque image. Un immense merci pour avoir si bien immortalisé cette journée — nous serons fiers de la partager avec nos futurs enfants grâce à tes photos.",
      author: 'Jade',
    },
    {
      text:
        "Nos photos de mariage sont sublimes ! Nous n’avons eu que des compliments de la part de nos invités. Ludo nous a mis à l’aise devant l’objectif et nous a offert des moments inoubliables pendant les séances. Je le recommande de tout cœur pour sa gentillesse et la qualité de son travail ! Nos albums sont incroyables — et en prime, fièrement fabriqués en France.",
      author: 'Soline',
    },
  ];
  const reviews = lang === 'fr' ? reviewsFR : reviewsEN;
  const nextReview = () => setCurrentReview((prev) => (prev + 1) % reviews.length);
  const prevReview = () => setCurrentReview((prev) => (prev - 1 + reviews.length) % reviews.length);

  return (
    <SectionContainer ref={ref}>
      <ContentWrapper>
        <SectionHeader>
          <Title
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            {lang === 'fr' ? 'Découvrir les histoires de mariage' : 'Explore Wedding Tales'}
          </Title>
        </SectionHeader>
        <GalleryRow>
          {shoots.map((shoot, index) => (
            <GalleryItem
              key={shoot.id}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
            >
              <ShootLink to={pathFor('gallery', lang, { slug: SHOOT_TO_SLUG[shoot.caption] || '' })} aria-label={`Open gallery: ${shoot.caption}`}>
                <ImageBox>
                  <StyledImage src={shoot.image} alt={shoot.caption} />
                </ImageBox>
              </ShootLink>
              <Caption>{shoot.caption}</Caption>
            </GalleryItem>
          ))}
        </GalleryRow>

        <ButtonLink to={pathFor('portfolio', lang)} aria-label="Go to portfolio">
          <Button
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
          >
            {lang === 'fr' ? 'Voir le portfolio' : 'More Stories'}
          </Button>
        </ButtonLink>

        <DistinctionsWrap>
          <Marquee>
            <Track>
              {repeated.map((src, i) => (
                <Badge key={`a-${i}`} src={src} alt={src.split('/').pop()} />
              ))}
            </Track>
          </Marquee>
        </DistinctionsWrap>

        <Separator />

        <ReviewsWrap>
          <PrevButton onClick={prevReview} aria-label="Previous review">‹</PrevButton>
          <ReviewViewport>
            <AnimatePresence initial={false} mode="wait">
              <motion.div
                key={currentReview}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.35 }}
              >
                <Review>
                  <ReviewText>“{reviews[currentReview].text}”</ReviewText>
                  <ReviewAuthor>{reviews[currentReview].author}</ReviewAuthor>
                </Review>
              </motion.div>
            </AnimatePresence>
          </ReviewViewport>
          <NextButton onClick={nextReview} aria-label="Next review">›</NextButton>
        </ReviewsWrap>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default StylizedShootSection;
